/**
 * The screen's wiring: the document, the exercise catalogue, the phase in the URL, and the view
 * model that falls out of the three.
 *
 * Decides nothing itself. Every rule it appears to apply lives in an entity's `model/` or in this
 * slice's `model/`, which is why `PlanBuilderScreen` can be read without opening this file.
 *
 * ── This is where the two entities are composed, and that is the only place they may be ──────
 * Execution ADR §2: entities of the same layer may not import each other, so the muscle rollup in
 * `plan-draft` takes attribution as a PARAMETER and `exercise-library` supplies it. Neither knows
 * the other exists. A page can see both, so the composition happens here — and because it happens
 * here rather than inside either entity, both stay testable on their own.
 *
 * ── The catalogue's failure is not the plan's failure ────────────────────────────────────────
 * The two reads are deliberately not combined into one status. If the library read fails or is
 * still in flight, the canvas still renders: the muscle bars are simply absent, because an exercise
 * the attribution map does not carry contributes nothing rather than zero. That is the honest
 * degradation — a trainer who can see their plan without the volume summary has lost a panel, and
 * a trainer shown an error screen because a secondary read failed has lost the plan.
 *
 * ── Two canvases, one status ─────────────────────────────────────────────────────────────────
 * `'sequence'` used to be a STATE of this screen: a sequential plan rendered a worded placeholder
 * because the seven-column grid could not describe it. The tape describes it, so the state is gone
 * and the scheduling mode chooses a canvas instead. `week` and `tape` are exclusive — exactly one is
 * non-null in the `ready` state — and that exclusivity is load-bearing beyond tidiness: each canvas
 * binds a drag MONITOR, and two mounted at once would dispatch a single drop twice.
 */

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  addDay,
  countedDays,
  countedTapeDays,
  displayGroupOf,
  muscleLoadByDay,
  phaseSummaries,
  renameSession,
  resolvePhaseIndex,
  setPlanCategory,
  setRestDaysAfter,
  tapeProjection,
  weekProjection,
} from '~/entities/plan-draft';
import {
  exerciseLibraryQueryOptions,
  heroStillUrl,
  toExerciseListItems,
  toMuscleAttribution,
} from '~/entities/exercise-library';
import { useContentLanguage } from '~/shared/i18n/contentLanguage';

import type { ExerciseChrome } from '../model/dayCardView';
import { toPlanMeta, type PlanMetaView } from '../model/planMetaView';
import { toTapeViewModel, type TapeViewModel } from '../model/tapeViewModel';
import { toWeekViewModel, type WeekViewModel } from '../model/weekViewModel';
import type { PlanMetaActions } from '../ui/PlanMetaPopover';

import { usePlanBuilderSearch } from './usePlanBuilderSearch';
import {
  usePlanDocument,
  type PlanBuilderTarget,
  type PlanDocumentState,
} from './usePlanDocument';

export interface PlanBuilderState {
  readonly status: PlanDocumentState['status'] | 'empty';
  /** The weekly canvas, or `null` — on a self-paced plan and in every state but `ready`. */
  readonly week: WeekViewModel | null;
  /** The self-paced canvas, or `null` — on a weekly plan and in every state but `ready`. */
  readonly tape: TapeViewModel | null;
  /**
   * The plan's name as soon as it is known, INDEPENDENT of `status`. The heading and the document
   * title need it while the tree is still loading and even when the plan is refused — a screen
   * that says "Ładowanie…" in its `h1` gives the trainer nothing to tell two tabs apart by.
   */
  readonly planName: string | null;
  readonly refusal: PlanDocumentState['refusal'];
  readonly retry: () => void;
  readonly selectPhase: (index: number) => void;
  /**
   * The phase the canvas is showing — already RESOLVED against the plan that loaded, not the raw
   * `?phase=`. Every surface addressing a phase takes this one answer, which is what stopped the
   * band dispatching into a phase index the projection had silently clamped away from.
   */
  readonly phaseIndex: number;
  /** The plan's name, category and cover, worded. `null` while there is no document. */
  readonly meta: PlanMetaView | null;
  /** Writing the category and the cover. `null` when the plan cannot be edited. */
  readonly setPlanMeta: PlanMetaActions | null;
  /**
   * Rename a day's workout, or `null` when there is no editable document — every state but `ready`.
   *
   * The store's own named operation, handed through rather than wrapped. It is `null`-or-the-function
   * rather than a function that no-ops, because the card renders a plain heading when it cannot be
   * edited: a control that silently does nothing is worse than one that is not offered.
   */
  readonly renameSession: ((slotToken: string, name: string) => void) | null;
  /**
   * Create a training day in the phase in view, and answer with its slot token so the caller can
   * open the dock on the day it just made.
   *
   * The weekday is `null` on a self-paced plan, where a day is appended to the cycle rather than
   * placed. The PHASE is bound here rather than taken as an argument, because the phase in view is
   * this hook's own answer and a caller passing its own would be a second source of truth for the
   * thing the tab bar decides.
   */
  readonly addDay: ((weekday: number | null) => string | null) | null;
  /** Move the gap after a day — the tape's rest stepper. `null` when the plan cannot be edited. */
  readonly setRestDaysAfter: ((daySlotToken: string, days: number) => void) | null;
}

export function usePlanBuilder(target: PlanBuilderTarget): PlanBuilderState {
  const { t, i18n } = useTranslation();
  // Two languages, never one: the catalogue is asked for in the CONTENT language the trainer chose,
  // while the reader's own locale words and sorts the view models.
  const contentLang = useContentLanguage();
  const locale = i18n.language;

  const document = usePlanDocument(target);
  const search = usePlanBuilderSearch();

  // Not gated on the document: the catalogue is the same list for every plan, so starting both at
  // once means the bars are ready when the tree is. Its `staleTime` makes this free after the
  // first screen in a session.
  const library = useQuery(exerciseLibraryQueryOptions(contentLang));

  const draft = document.draft;
  // A refusal still knows the plan's name — only the tree's SHAPE was unusable — so the heading
  // names the plan rather than falling back to its id.
  const planName =
    draft !== null ? draft.name.trim() || null : (document.refusal?.planName ?? null);

  /**
   * The phase in view, resolved against the plan that actually loaded — ONE answer, spent on both
   * the projection and every operation addressed at a phase.
   *
   * The projections have always clamped internally, so the canvas and its add GATE were reading a
   * real phase while `addDay` was handed the raw `?phase=` straight from the URL. `/plans/91?phase=99`
   * therefore drew a tape of phase 3 with an enabled "+ Trening" that dispatched into phase 98 and
   * did nothing at all — the operation's own bounds check absorbed it silently, so the press looked
   * like a broken button. The same shape existed on the week canvas.
   *
   * Resolved HERE rather than in `usePlanBuilderSearch`, which is the obvious home and cannot do it:
   * that hook reads the address and nothing else, and clamping needs the phase COUNT, which only
   * exists once the document has loaded. Putting the plan into the address hook would make every
   * caller of the URL depend on the read.
   */
  const phaseIndex =
    draft === null ? search.phaseIndex : (resolvePhaseIndex(draft, search.phaseIndex) ?? 0);

  const base = {
    planName,
    refusal: document.refusal,
    retry: document.retry,
    phaseIndex,
    meta: draft === null ? null : toPlanMeta(t, draft),
    setPlanMeta: document.readOnly
      ? null
      : { setCategory: setPlanCategory },
    selectPhase: (index: number) => {
      search.update({ phase: index + 1 });
    },
    renameSession: document.readOnly ? null : renameSession,
    addDay: document.readOnly ? null : (weekday: number | null) => addDay(phaseIndex, weekday),
    setRestDaysAfter: document.readOnly ? null : setRestDaysAfter,
  };

  if (draft === null) {
    return { ...base, status: document.status, week: null, tape: null };
  }

  const catalogue = toExerciseListItems(library.data);
  const attribution = toMuscleAttribution(catalogue);
  /**
   * Whether the CATALOGUE read is still open. One consumer: the volume rail.
   *
   * The two reads stay uncombined — the invariant above is that "the catalogue's failure is not the
   * plan's failure", and the week still renders with the muscle chips simply absent. What the rail
   * cannot do is state a number: an empty attribution makes every group zero, so six cells read `0`
   * and "poniżej MEV" over a fully programmed week and then silently rewrote themselves. It waits,
   * with its box reserved. A FAILED read is not a wait — the rail then draws the honest zero it can
   * derive, which is the same degradation the chips take.
   */
  const isAttributionPending = library.isPending;
  // One pass over the catalogue, keyed the way the draft addresses an exercise. Built here for the
  // same reason `attribution` is: this is the one place the two entities may be composed.
  // The photograph first, the demo video's 3D poster frame when there is no photograph, and the
  // placeholder only when the catalogue names neither.
  const coverById = new Map(catalogue.map((item) => [item.id, heroStillUrl(item)]));

  /**
   * What the catalogue knows about one movement — the primary group's colour and its photograph.
   *
   * The chip names the exercise's PRIMARY group, the first one as the backend ordered them: a
   * secondary contributes half a set to the bars but is not what the movement is for, and a chip
   * listing three groups would be unreadable at 8.5px in a 232px tile. An exercise the read has not
   * answered for yet — or never will, because the read failed — answers `null` on both, and the card
   * degrades to a name.
   */
  const lookUpExercise = (exerciseId: number): ExerciseChrome => {
    const first = attribution.get(exerciseId)?.primary[0];
    return {
      group: first === undefined ? null : displayGroupOf(first),
      coverUrl: coverById.get(exerciseId) ?? null,
    };
  };

  const phases = phaseSummaries(draft);

  if (draft.mode === 'selfpaced') {
    const projection = tapeProjection(draft, phaseIndex);
    // A plan with no phases at all. Real — `pages/plan-library` already renders "Pusty program" for
    // one — and it needs its own words rather than an empty tape.
    if (projection === null) return { ...base, status: 'empty', week: null, tape: null };
    return {
      ...base,
      status: 'ready',
      week: null,
      tape: toTapeViewModel({
        draft,
        projection,
        phases,
        // The days the load covers, and the order they come in, are the PROJECTION's answer — the
        // view model walks them back with one cursor, which is what keeps a day's bars under that
        // day rather than under its neighbour.
        load: muscleLoadByDay(countedTapeDays(projection), attribution),
        lookUpExercise,
        isAttributionPending,
        t,
        locale,
      }),
    };
  }

  const projection = weekProjection(draft, phaseIndex);
  if (projection === null) return { ...base, status: 'empty', week: null, tape: null };

  return {
    ...base,
    status: 'ready',
    tape: null,
    week: toWeekViewModel({
      draft,
      projection,
      phases,
      load: muscleLoadByDay(countedDays(projection), attribution),
      lookUpExercise,
      isAttributionPending,
      t,
      locale,
    }),
  };
}
